/**
 * ContasPage Component
 * 
 * Página de gerenciamento de Contas Contábeis.
 * Permite criar, editar e gerenciar contas do plano de contas.
 * 
 * Funcionalidades:
 * - Listagem de contas contábeis
 * - Criação de contas (cofre, caixinha, pagbank, etc)
 * - Edição de contas
 * - Filtros por tipo
 */

import { useState, useEffect, useCallback } from 'react'
import { Plus, Edit, Trash2, Search, DollarSign, TrendingUp, TrendingDown, Wallet } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { financialAccountService } from '../../../../services/erpApi'
import './ContasPage.css'

function ContasPage() {
  const [contas, setContas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingConta, setEditingConta] = useState(null)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: 'asset',
    parentId: null,
    active: true,
    description: '',
    accountType: 'physical'
  })
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    active: ''
  })

  const loadContas = useCallback(async () => {
    setLoading(true)
    try {
      // Preparar filtros para a API (remover strings vazias)
      const apiFilters = {}
      if (filters.search) apiFilters.search = filters.search
      if (filters.type) apiFilters.type = filters.type
      if (filters.active !== '') apiFilters.active = filters.active
      
      const response = await financialAccountService.getAll(apiFilters)
      if (response.success) {
        console.log('Contas carregadas:', response.data)
        setContas(response.data || [])
      } else {
        console.error('Erro na resposta:', response)
      }
    } catch (error) {
      console.error('Erro ao carregar contas:', error)
      alert('Erro ao carregar contas. Verifique o console para mais detalhes.')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadContas()
  }, [loadContas])

  const handleCreate = () => {
    setEditingConta(null)
    setFormData({
      code: '',
      name: '',
      type: 'asset',
      parentId: null,
      active: true,
      description: '',
      accountType: 'physical'
    })
    setErrors({})
    setModalOpen(true)
  }

  const handleEdit = (conta) => {
    setEditingConta(conta)
    setFormData({
      code: conta.code,
      name: conta.name,
      type: conta.type,
      parentId: conta.parent_id || null,
      active: conta.active !== undefined ? conta.active : true,
      description: conta.description || '',
      accountType: conta.account_type || 'physical'
    })
    setErrors({})
    setModalOpen(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) {
      return
    }

    try {
      const response = await financialAccountService.delete(id)
      if (response.success) {
        alert('Conta excluída com sucesso!')
        loadContas()
      }
    } catch (error) {
      console.error('Erro ao excluir conta:', error)
      alert(error.response?.data?.message || 'Erro ao excluir conta')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    // Validações
    const newErrors = {}
    if (!formData.code.trim()) {
      newErrors.code = 'Código é obrigatório'
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      // Preparar dados para envio
      const dataToSend = {
        name: formData.name.trim(),
        type: formData.type,
        parentId: formData.parentId || null,
        active: formData.active !== undefined ? formData.active : true,
        description: formData.description?.trim() || null,
        accountType: formData.accountType || 'physical'
      }

      // Se estiver editando, só enviar código se ele mudou
      if (editingConta) {
        // Se o código mudou, incluir no update
        if (formData.code.trim() !== editingConta.code) {
          dataToSend.code = formData.code.trim()
        }
        const response = await financialAccountService.update(editingConta.id, dataToSend)
        if (response.success) {
          alert('Conta atualizada com sucesso!')
          setModalOpen(false)
          // Limpar filtros para garantir que a conta apareça
          setFilters({ search: '', type: '', active: '' })
          // O useEffect vai recarregar automaticamente quando os filtros mudarem
        } else {
          alert(response.message || 'Erro ao atualizar conta')
        }
      } else {
        // Ao criar, código é obrigatório
        if (!formData.code || !formData.code.trim()) {
          setErrors({ code: 'Código é obrigatório' })
          return
        }
        dataToSend.code = formData.code.trim()
        const response = await financialAccountService.create(dataToSend)
        if (response.success) {
          alert('Conta criada com sucesso!')
          setModalOpen(false)
          // Limpar filtros para garantir que a conta apareça
          setFilters({ search: '', type: '', active: '' })
          // O useEffect vai recarregar automaticamente quando os filtros mudarem
        } else {
          alert(response.message || 'Erro ao criar conta')
        }
      }
    } catch (error) {
      console.error('Erro ao salvar conta:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar conta'
      alert(errorMessage)
      
      // Se houver erros de validação, exibir
      if (error.response?.data?.errors) {
        const validationErrors = {}
        error.response.data.errors.forEach(err => {
          validationErrors[err.param || err.path] = err.msg || err.message
        })
        setErrors(validationErrors)
      }
    }
  }

  const getTypeLabel = (type) => {
    const labels = {
      asset: 'Ativo',
      liability: 'Passivo',
      equity: 'Patrimônio',
      revenue: 'Receita',
      expense: 'Despesa'
    }
    return labels[type] || type
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'asset':
        return <Wallet size={18} />
      case 'revenue':
        return <TrendingUp size={18} />
      case 'expense':
        return <TrendingDown size={18} />
      default:
        return <DollarSign size={18} />
    }
  }

  const columns = [
    {
      key: 'code',
      label: 'Código',
      render: (value) => <strong>{value}</strong>
    },
    {
      key: 'name',
      label: 'Nome',
      render: (value, row) => (
        <div className="account-name-cell">
          {getTypeIcon(row.type)}
          <span>{value}</span>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (value) => (
        <span className={`type-badge type-${value}`}>
          {getTypeLabel(value)}
        </span>
      )
    },
    {
      key: 'active',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Ativa' : 'Inativa'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (_, row) => (
        <div className="table-actions">
          <button
            className="action-btn edit"
            onClick={() => handleEdit(row)}
            title="Editar"
          >
            <Edit size={16} />
          </button>
          <button
            className="action-btn delete"
            onClick={() => handleDelete(row.id)}
            title="Excluir"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ]

  return (
    <div className="contas-page">
      <div className="page-header">
        <div>
          <h1>Contas Contábeis</h1>
          <p>Gerencie as contas do plano de contas</p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus size={18} />
          Nova Conta
        </button>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <div className="filter-group">
          <Search className="filter-icon" />
          <input
            type="text"
            className="filter-input"
            placeholder="Buscar por código ou nome..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          >
            <option value="">Todos os tipos</option>
            <option value="asset">Ativo</option>
            <option value="liability">Passivo</option>
            <option value="equity">Patrimônio</option>
            <option value="revenue">Receita</option>
            <option value="expense">Despesa</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filters.active}
            onChange={(e) => setFilters({ ...filters, active: e.target.value })}
          >
            <option value="">Todos os status</option>
            <option value="true">Ativas</option>
            <option value="false">Inativas</option>
          </select>
        </div>

        <button
          className="btn-clear-filters"
          onClick={() => setFilters({ search: '', type: '', active: '' })}
        >
          Limpar Filtros
        </button>
      </div>

      {/* Tabela */}
      <div className="table-container">
        <DataTable
          data={contas}
          columns={columns}
          loading={loading}
          emptyMessage="Nenhuma conta encontrada"
        />
      </div>

      {/* Modal de Criação/Edição */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingConta ? 'Editar Conta' : 'Nova Conta'}
        size="medium"
        footer={
          <div className="modal-footer-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {editingConta ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="conta-form">
          <div className="form-row">
            <FormField
              label="Código"
              name="code"
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="Ex: CAIXINHA, COFRE, PAGBANK"
              disabled={!!editingConta}
              required
              error={errors.code}
            />

            <FormField
              label="Nome"
              name="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nome da conta"
              required
              error={errors.name}
            />
          </div>

          <div className="form-row">
            <FormField
              label="Tipo"
              name="type"
              type="select"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              options={[
                { value: 'asset', label: 'Ativo' },
                { value: 'liability', label: 'Passivo' },
                { value: 'equity', label: 'Patrimônio' },
                { value: 'revenue', label: 'Receita' },
                { value: 'expense', label: 'Despesa' }
              ]}
            />

            <FormField
              label="Status"
              name="active"
              type="select"
              value={formData.active ? 'true' : 'false'}
              onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
              options={[
                { value: 'true', label: 'Ativa' },
                { value: 'false', label: 'Inativa' }
              ]}
            />
          </div>

          <div className="form-row">
            <FormField
              label="Tipo de Conta"
              name="accountType"
              type="select"
              value={formData.accountType}
              onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
              required
              options={[
                { value: 'physical', label: 'Física (Caixinha, Cofre, etc)' },
                { value: 'digital', label: 'Digital (Conta Bancária, PIX, etc)' }
              ]}
              helpText="Contas físicas são para dinheiro em espécie. Contas digitais podem importar extratos bancários (OFX)."
            />
          </div>

          <FormField
            label="Descrição"
            name="description"
            type="textarea"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descrição da conta (opcional)"
            rows={3}
          />
        </form>
      </Modal>
    </div>
  )
}

export default ContasPage

